/* eslint-disable promise/catch-or-return */
import PortalIdParser from 'PortalIdParser';
import { gate } from 'hub-http/gates';
import { buildUrl } from 'hub-http/helpers/url';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import UpgradeProductProperties, { UpgradeProductPropertiesFromApiName } from 'self-service-api/constants/UpgradeProductProperties';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getUpgradePageUrl, productsAndAddons, trialPricingPage } from 'self-service-api/core/utilities/links';
import { getHubAndTierFromApiName, getUpgradeDataTrackingProperties, mapPurchaseMotionApiName, trackPotentialBannerInteraction } from 'ui-addon-upgrades/ums/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import Raven from 'raven-js';
import withErrorBoundary from '../ErrorBoundary';
import { getUserInfo } from '../_core/common/api/getUserInfo';
import { UI_PRICING_PAGE_REDIRECT_BUTTON } from '../_core/common/constants/ErrorBoundaryNames';
import { sourceKeys } from '../_core/common/data/upgradeData/properties/sources';
import { getSource } from '../_core/common/data/upgradeData/transformers/getPropertySource';
import { PRICING_PAGE_REDIRECT } from '../_core/purchaseMotions/PurchaseMotionTypes';
import { trackFailedApiNameMap, trackPurchaseMotionFailure } from '../_core/utils/metricUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const UIPricingPageRedirectButton = ({
  upgradeData,
  level,
  isUpgradePoint,
  use = 'primary',
  onClick,
  subscreen,
  children,
  usageLimitBannerData,
  goToUpgradePage,
  goToTrialPricingPage,
  size,
  disabled,
  responsive,
  ['data-test-id']: dataTestId = 'pricing-page-redirect-button',
  ['data-selenium']: dataSelenium = 'pricing-page-redirect-button',
  ['data-selenium-id']: dataSeleniumId = 'pricing-page-redirect-button',
  truncate,
  block
}) => {
  const _isMounted = useRef(true);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [userInfo, setUserInfo] = useState();
  const upgradeDataWithApiName = useMemo(() => {
    return Object.assign({}, upgradeData, {
      apiName: mapPurchaseMotionApiName(true, upgradeData.upgradeProduct, 'UIPricingPageRedirectButton', upgradeData.apiName),
      source: getSource({
        app: upgradeData.app,
        screen: upgradeData.screen,
        uniqueId: upgradeData.uniqueId
      }, sourceKeys.GENERAL)
    });
  }, [upgradeData]);
  const getUpgradeLink = () => {
    if (upgradeDataWithApiName.apiName) {
      const mappedProperties = UpgradeProductPropertiesFromApiName[upgradeDataWithApiName.apiName];
      return mappedProperties && mappedProperties.upgradeLink ? mappedProperties.upgradeLink : undefined;
    }
    return (UpgradeProductProperties[upgradeDataWithApiName.upgradeProduct] || {}).upgradeLink;
  };
  const upgradeLink = getUpgradeLink();
  useEffect(() => {
    if (!hasTrackedView) {
      upgradeDataWithApiName.apiName = mapPurchaseMotionApiName(true, upgradeDataWithApiName.upgradeProduct, 'UIPricingPageRedirectButton', upgradeDataWithApiName.apiName);
      track('pricingPageRedirectButtonInteraction', {
        action: 'viewed',
        upgradeProduct: upgradeDataWithApiName.upgradeProduct,
        source: upgradeDataWithApiName.source,
        subscreen,
        apiName: upgradeDataWithApiName.apiName
      });
      if (isUpgradePoint) {
        track('interaction', Object.assign({
          action: 'viewed pricing page redirect',
          isPrimaryCta: level === 1,
          isSecondaryCta: level === 2
        }, usageLimitBannerData, getUpgradeDataTrackingProperties(upgradeDataWithApiName)));
      }
      setHasTrackedView(true);
    }
  }, [upgradeDataWithApiName, subscreen, isUpgradePoint, level, usageLimitBannerData, hasTrackedView]);
  useEffect(() => {
    return function cleanup() {
      _isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    getUserInfo().then(setUserInfo).catch(e => Raven.captureException(e));
  }, []);
  const hasTrialUpgradeMigrationGate = useMemo(() => userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates.includes(gate('HiC:TrialUpgradePage:Migration')), [userInfo]);
  const apiNameToUse = upgradeDataWithApiName.apiName || UpgradeProductToApiNameMap[upgradeDataWithApiName.upgradeProduct];
  const getRedirectLink = () => {
    if (goToTrialPricingPage) {
      return hasTrialUpgradeMigrationGate ? buildUrl({
        path: `/trial-pricing/${PortalIdParser.get()}/upgrade`,
        query: `apiName=${apiNameToUse}`
      }) : trialPricingPage(upgradeDataWithApiName.uniqueId, apiNameToUse);
    }

    // For Upgrade page
    // this is also used in the UMS banners
    if (goToUpgradePage) {
      if (usageLimitBannerData) {
        return getUpgradePageUrl(upgradeDataWithApiName.uniqueId, {
          limit: usageLimitBannerData.limit,
          value: usageLimitBannerData.value
        });
      }
      return getUpgradePageUrl(upgradeDataWithApiName.uniqueId);
    }
    if (!apiNameToUse) {
      trackFailedApiNameMap('getRedirectLink', upgradeDataWithApiName.apiName || 'no api name', upgradeDataWithApiName.upgradeProduct);
      Raven.captureException(new Error(`failed to get redirect link from apiName ${upgradeDataWithApiName.apiName} in getRedirectLink`));
      trackPurchaseMotionFailure({
        uniqueId: upgradeData.uniqueId,
        apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
        purchaseMotion: PRICING_PAGE_REDIRECT
      });
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return upgradeLink(upgradeDataWithApiName.uniqueId);
    }
    if (upgradeLink) {
      const {
        tier
      } = getHubAndTierFromApiName(apiNameToUse);
      return upgradeLink(upgradeDataWithApiName.uniqueId, tier);
    }
    return productsAndAddons(); //fallback generic pricing page link
  };
  const handleUpgrade = () => {
    track('pricingPageRedirectButtonInteraction', {
      action: 'clicked',
      upgradeProduct: upgradeDataWithApiName.upgradeProduct,
      source: upgradeDataWithApiName.source,
      subscreen,
      apiName: upgradeDataWithApiName.apiName
    });
    if (isUpgradePoint) {
      track('interaction', Object.assign({
        action: 'clicked upgrade point'
      }, usageLimitBannerData, getUpgradeDataTrackingProperties(upgradeDataWithApiName)));
      track('interaction', Object.assign({
        action: 'clicked pricing page redirect',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, usageLimitBannerData, getUpgradeDataTrackingProperties(upgradeDataWithApiName)));
      const {
        pointType,
        upgradeKey
      } = upgradeDataWithApiName;
      trackPotentialBannerInteraction(pointType, upgradeKey).catch(error => {
        Raven.captureException(new Error('Failed to submit interaction from UIPricingPageRedirectButton'), {
          extra: {
            message: error.message,
            uniqueId: upgradeData.uniqueId
          }
        });
      });
    }
    if (onClick) onClick();
  };
  if (!upgradeLink) return null;
  return /*#__PURE__*/_jsx(Button, {
    target: "_blank",
    "data-selenium": dataSelenium,
    "data-test-id": dataTestId,
    "data-selenium-id": dataSeleniumId,
    onClick: handleUpgrade,
    href: getRedirectLink(),
    use: use,
    size: size,
    disabled: disabled,
    responsive: responsive,
    truncate: truncate,
    block: block,
    truncateStringProps: {
      justifyContent: 'center'
    },
    children: children || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "purchaseMotions.pricingPageRedirect.button.defaultButtonCopy"
    })
  });
};
export default withErrorBoundary(UIPricingPageRedirectButton, {
  boundaryName: UI_PRICING_PAGE_REDIRECT_BUTTON
});